import { Layer, Popup, Source } from "@vis.gl/react-maplibre";
import type { MapGeoJSONFeature } from "maplibre-gl";
import { FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import { useMapLibreLayerPointer } from "@ryde/controls";
import { TrackingPointTooltipContent } from "@ryde/layout";
import {
  NO_VALUE_DASH,
  TLatLngTuple,
  TLngLatTuple,
  useFormats,
} from "@ryde/shared";
import { rydeColors } from "@ryde/theme";

import {
  INVESTIGATION_DOTS_LAYER_ID,
  INVESTIGATION_DOTS_SOURCE_ID,
  TRAIL_DOT_BORDER_WEIGHT,
  TRAIL_DOT_RADIUS,
  TRAIL_POINT_COLOR,
} from "../investigation.constants";
import { ITrailDot } from "../investigation.models";

/**
 * GPS trail sample dots as one GPU circle layer, with the Leaflet tooltip replaced by a MapLibre popup
 * driven by layer-scoped pointer events. Labels are precomputed into the feature properties, so hovering
 * needs no hit test — the same approach the live map's trail dots use, down to the tooltip card itself.
 */
export const InvestigationTrailDots: FC<{ dots: ITrailDot[] }> = ({ dots }) => {
  const { t } = useTranslation();
  const { formatTimeWithSecondsNormalized } = useFormats();
  const [hoveredPoint, setHoveredPoint] = useState<{
    longitude: number;
    latitude: number;
    time: string;
    speed: string;
  } | null>(null);

  const featureCollection = useMemo(
    () => ({
      type: "FeatureCollection" as const,
      features: dots.map(dot => {
        const [lat, lng] = dot.position as TLatLngTuple;
        const speedLabel =
          dot.speedKmh === undefined ||
          dot.speedKmh === null ||
          dot.speedKmh < 0
            ? NO_VALUE_DASH
            : t("rideMap.TRACKING_SPEED", { speed: dot.speedKmh });
        const timeLabel = formatTimeWithSecondsNormalized(
          new Date(dot.tMs).toISOString() as TDateISO,
        );

        return {
          type: "Feature" as const,
          properties: { time: timeLabel, speed: speedLabel },
          geometry: {
            type: "Point" as const,
            coordinates: [lng, lat] as TLngLatTuple,
          },
        };
      }),
    }),
    [dots, t, formatTimeWithSecondsNormalized],
  );

  const hasDots = dots.length > 0;

  const handleHover = useEvent((feature: MapGeoJSONFeature): void => {
    if (feature.geometry.type !== "Point") {
      return;
    }

    const [longitude, latitude] = feature.geometry.coordinates as [
      number,
      number,
    ];

    setHoveredPoint({
      longitude,
      latitude,
      time: String(feature.properties?.time ?? ""),
      speed: String(feature.properties?.speed ?? NO_VALUE_DASH),
    });
  });
  const handleLeave = useEvent(() => setHoveredPoint(null));

  useMapLibreLayerPointer({
    layerId: INVESTIGATION_DOTS_LAYER_ID,
    enabled: hasDots,
    onHover: handleHover,
    onLeave: handleLeave,
  });

  if (!hasDots) {
    return null;
  }

  return (
    <>
      <Source
        id={INVESTIGATION_DOTS_SOURCE_ID}
        type="geojson"
        data={featureCollection}
      >
        <Layer
          id={INVESTIGATION_DOTS_LAYER_ID}
          type="circle"
          paint={{
            ["circle-radius"]: TRAIL_DOT_RADIUS,
            ["circle-color"]: TRAIL_POINT_COLOR,
            ["circle-stroke-width"]: TRAIL_DOT_BORDER_WEIGHT,
            ["circle-stroke-color"]: rydeColors.white,
          }}
        />
      </Source>

      {hoveredPoint && (
        <Popup
          longitude={hoveredPoint.longitude}
          latitude={hoveredPoint.latitude}
          anchor="bottom"
          closeButton={false}
          closeOnClick={false}
          className="custom-tracking-tooltip"
        >
          <TrackingPointTooltipContent
            time={hoveredPoint.time}
            speed={hoveredPoint.speed}
          />
        </Popup>
      )}
    </>
  );
};
